import { applyContextOptions } from "@gameApp/helpers/canvas/contextOptions";
import { drawTextPure } from "@gameApp/helpers/canvas/draw/textPure";
import { measureText } from "@gameApp/helpers/canvas/measureText";
import type { BasicDrawOptions } from "@gameApp/helpers/canvas/types";
import { MINI_GAME_CHARTS_TIMELINE_DATES_GAP_BEFORE_HIDE } from "@gameComponents/MiniGame/Charts/constants";
import {
  type CountableTimeInterval,
  type ScaleTime,
  timeDay,
  timeHour,
  timeMinute,
  timeMonth,
  timeWeek,
  timeYear,
} from "d3";

interface Params extends BasicDrawOptions {
  scale: ScaleTime<number, number>;
  boxWidth: number;
  boxHeight: number;
}

function getTrueEveryTick(scale: ScaleTime<number, number>, step: number) {
  const [, maxScaleRangeValue] = scale.range();
  const split = maxScaleRangeValue / step;

  return Array.from({ length: step }, (_, i) => {
    return scale.invert(split * i);
  });
}

function getTicksIntervalsAndFormatFn(
  scale: ScaleTime<number, number>,
): [Date[], (d: Date) => string] {
  const interval = <[Date, Date]>scale.domain();
  const yearsCount = timeYear.count(...interval);
  const monthsCount = timeMonth.count(...interval);
  const weeksCount = timeWeek.count(...interval);
  const daysCount = timeDay.count(...interval);
  const hoursCount = timeHour.count(...interval);
  const minutesCount = timeMinute.count(...interval);
  const interval0 = hoursCount > 3 && hoursCount <= 4;
  const interval1 = hoursCount > 4 && hoursCount <= 12;
  const interval2 = hoursCount === 0 && minutesCount > 0;
  const interval3 = hoursCount >= 1 && hoursCount <= 5;
  const interval4 = hoursCount >= 6 && hoursCount <= 17;
  const interval5 = hoursCount >= 18 && hoursCount < 48;
  const interval6 = hoursCount >= 48 && daysCount <= 3;
  const interval7 = daysCount > 3 && weeksCount <= 2;
  const interval8 = weeksCount >= 3 && monthsCount <= 1;
  const interval9 = monthsCount > 1 && monthsCount < 12;
  const interval10 = monthsCount >= 12 && yearsCount < 3;
  const interval11 = yearsCount >= 3;
  let formatFn: (d: Date) => string;
  let ticks: Date[];

  switch (true) {
    case interval0:
      ticks = scale.ticks(timeHour.every(1) as CountableTimeInterval);
      break;
    case interval1:
      ticks = scale.ticks(timeHour.every(3) as CountableTimeInterval);
      break;
    case interval2:
      ticks = scale.ticks(timeMinute.every(30) as CountableTimeInterval);
      break;
    case interval3:
      ticks = scale.ticks(timeHour.every(2) as CountableTimeInterval);
      break;
    case interval4:
      ticks = scale.ticks(timeHour.every(4) as CountableTimeInterval);
      break;
    case interval5:
      ticks = scale.ticks(timeHour.every(6) as CountableTimeInterval);
      break;
    case interval6:
      ticks = scale.ticks(timeHour.every(24) as CountableTimeInterval);
      break;
    case interval7:
      ticks = getTrueEveryTick(scale, 5);
      break;
    case interval8:
      ticks = getTrueEveryTick(scale, 5);
      break;
    case interval9:
      ticks = getTrueEveryTick(scale, 5);
      break;
    case interval10:
      ticks = getTrueEveryTick(scale, 5);
      break;
    case interval11:
      ticks = getTrueEveryTick(scale, 5);
      break;
    default:
      ticks = getTrueEveryTick(scale, 5);
  }

  switch (true) {
    case interval6:
      formatFn = scale.tickFormat(timeDay, "%b %d");
      break;
    case interval7:
      formatFn = scale.tickFormat(timeDay, "%b %d");
      break;
    case interval8:
      formatFn = scale.tickFormat(timeWeek, "%b %d");
      break;
    case interval9:
      formatFn = scale.tickFormat(timeMonth, "%b %d");
      break;
    case interval10:
      formatFn = scale.tickFormat(timeMonth, "%b %Y");
      break;
    case interval11:
      formatFn = scale.tickFormat(timeYear, "%Y");
      break;
    default:
      formatFn = scale.tickFormat(timeDay, "%H:%M");
  }

  return [ticks, formatFn];
}

export function miniGameChartsDrawTimelineDates(params: Params): void {
  const { scale, context, contextOptions, boxWidth, translateX, translateY } =
    params;

  const [ticks, formatFn] = getTicksIntervalsAndFormatFn(scale);
  let prevTickValueEndPositionX: number | null = null;

  for (let i = 0; i < ticks.length; i += 1) {
    const tickValue = ticks[i];

    const { width: tickValueWidth } = measureText(
      formatFn(tickValue),
      (ctx) => {
        applyContextOptions({ context: ctx, contextOptions });
      },
    );

    const tickValueStartPositionX = Number(scale(tickValue));

    const tickValuePositionX =
      tickValueStartPositionX + tickValueWidth > boxWidth
        ? boxWidth - tickValueWidth
        : tickValueStartPositionX;

    if (
      (prevTickValueEndPositionX !== null &&
        tickValuePositionX - MINI_GAME_CHARTS_TIMELINE_DATES_GAP_BEFORE_HIDE >=
          prevTickValueEndPositionX) ||
      !prevTickValueEndPositionX
    ) {
      drawTextPure({
        text: formatFn(tickValue),
        x: tickValuePositionX,
        y: 0,
        translateX,
        translateY,
        context,
        contextOptions,
      });
    }

    prevTickValueEndPositionX = tickValuePositionX + tickValueWidth;
  }
}
